import { usePathname } from 'next/navigation'
import { PropsWithChildren } from 'react'
import { QueryClient, QueryClientProvider } from 'react-query'
import { devLog } from '../utils/devLogger'
import { STATUS_CODE } from '../constants/api-status-codes'
import { onMpinRequired } from '../utils/auth/on-mpin-required'
import { useAuth } from './auth'
import { NOT_LOGGED_IN } from '@/components/kyc/utils/errors'
import { IError } from '../utils/network-adapter/types'
import { shouldSkipAuthRedirect } from '../utils/auth/paths-helper'
import { logNewRelicEvent } from '../utils/common/custom/newrelic-page-events'
import { ErrorNRLogs } from '../utils/network-adapter/error.tracking'
import Cookies from 'js-cookie'
import { COOKIE_KEYS } from '../utils/auth/cookies'

const RETRY_COUNT = 3

/**
 * Type guard to check if error has statusCode property
 */
const hasStatusCode = (error: unknown): error is { statusCode: number } => {
  return (
    error !== null &&
    typeof error === 'object' &&
    'statusCode' in error &&
    typeof (error as { statusCode: unknown }).statusCode === 'number'
  )
}

/**
 * Check if error message contains auth-related status codes
 */
const isAuthErrorInMessage = (message: string): boolean => {
  return (
    message.includes('401') ||
    message.includes(NOT_LOGGED_IN) ||
    message.includes('424')
  )
}

/**
 * Check if error is an auth error (401 or 424)
 */
const isAuthError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false

  // Check if error has statusCode property
  if (hasStatusCode(error)) {
    return (
      error.statusCode === STATUS_CODE[401] ||
      error.statusCode === STATUS_CODE[424]
    )
  }

  // Check if it's an Error object with auth-related message
  if (error instanceof Error) {
    return isAuthErrorInMessage(error.message)
  }

  return false
}

/**
 * Extract status code from error
 */
const extractStatusCode = (error: unknown): number | null => {
  if (hasStatusCode(error)) {
    return error.statusCode
  }

  if (error instanceof Error) {
    const message = error.message
    if (message.includes('401') || message.includes(NOT_LOGGED_IN)) {
      return STATUS_CODE[401]
    }
    if (message.includes('424')) {
      return STATUS_CODE[424]
    }
  }

  return null
}

export default function ReactQueryProvider({
  children,
}: PropsWithChildren<{}>) {
  const { onInvalidAccess, isLoading, token } = useAuth()
  const pathname = usePathname()

  const handleError = (error: unknown) => {
    logNewRelicEvent(ErrorNRLogs.webFetchError, {
      source: 'react-query-provider',
      error: JSON.stringify(error),
      sessionId: Cookies.get(COOKIE_KEYS.TRACE_ID) ?? '',
      userId: Cookies.get(COOKIE_KEYS.USER_ID) ?? '',
      pathname,
    })

    const statusCode = extractStatusCode(error)

    if (
      statusCode &&
      token &&
      !pathname.includes('sso-handler') &&
      !pathname.includes('login') &&
      !isLoading
    ) {
      devLog('Processed error with statusCode:', statusCode, pathname)

      if (statusCode === STATUS_CODE[401]) {
        devLog('Clearing access token 401')
        onInvalidAccess()
      } else if (statusCode === STATUS_CODE[424]) {
        // Check if error has redirectUrl for 2FA
        if (error && typeof error === 'object' && 'redirectUrl' in error) {
          const iError = error as IError
          if (iError.redirectUrl && !shouldSkipAuthRedirect(pathname)) {
            devLog('SWR error 2Fa 424', iError.url, iError.apiResponse)
            onMpinRequired(iError.redirectUrl)
          }
        }
      }
    } else {
      // Log unhandled errors for debugging
      devLog('Unhandled error type:', typeof error, error)
    }
  }

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        onError: handleError,
        enabled: !isLoading,
        retry: (failureCount, error: unknown) => {
          devLog('ReactQuery Retry Attempt', failureCount, error)

          // Don't retry auth errors
          if (isAuthError(error)) {
            return false
          }

          // Retry up to 3 (RETRY_COUNT) times for other errors by default
          // This retry value is configurable in individual requests --> individual retry count will override this default value
          return failureCount < RETRY_COUNT
        },
      },
      mutations: {
        onError: handleError,
      },
    },
  })

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  )
}
